import { env } from "@/env";
import { logger } from "@utilities/logger";

const SERVER_URL = env.NEXT_PUBLIC_XI_SERVER_URL;

export type DialogueInput = {
  text: string;
  voice_id: string;
};

export type DialogueConfig = {
  hcaptcha_token?: null | string;
  inputs: DialogueInput[];
  language_code?: string;
};

export type Alignment = {
  character_end_times_seconds: number[];
  character_start_times_seconds: number[];
  characters: string[];
};

export type StreamResponse = {
  alignment: Alignment | null;
  normalized_alignment: Alignment | null;
};

type TimestampResponse = {
  alignment?: Alignment;
  audio_base64: string;
};

type StreamingTimestampResponse = {
  chunks: TimestampResponse[];
};

// Process data with timestamps (either from cache or API)
const processTimestampData = async (
  data: TimestampResponse | StreamingTimestampResponse,
  audioElement: HTMLAudioElement,
) => {
  let alignment: Alignment | undefined;
  let audioBlob: Blob | undefined;

  if ("chunks" in data) {
    const chunks = data.chunks;

    // Combine alignment
    alignment = chunks.reduce(
      (acc, chunk) => ({
        character_end_times_seconds: [
          ...(acc.character_end_times_seconds || []),
          ...(chunk.alignment?.character_end_times_seconds || []),
        ],
        character_start_times_seconds: [
          ...(acc.character_start_times_seconds || []),
          ...(chunk.alignment?.character_start_times_seconds || []),
        ],
        characters: [...(acc.characters || []), ...(chunk.alignment?.characters || [])],
      }),
      {
        character_end_times_seconds: [],
        character_start_times_seconds: [],
        characters: [],
      } as Alignment,
    );

    // Combine audio
    const audioArrays = chunks.map(chunk => {
      const binaryString = window.atob(chunk.audio_base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    });

    const totalLength = audioArrays.reduce((acc, curr) => acc + curr.length, 0);
    const combinedAudio = new Uint8Array(totalLength);
    let offset = 0;
    for (const arr of audioArrays) {
      combinedAudio.set(arr, offset);
      offset += arr.length;
    }
    audioBlob = new Blob([combinedAudio], { type: "audio/mpeg" });
  } else {
    alignment = data.alignment;
    if (data.audio_base64) {
      audioElement.src = `data:audio/mpeg;base64,${data.audio_base64}`;
      return { alignment };
    }
  }

  if (audioBlob) {
    return new Promise<{ alignment?: Alignment }>(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        audioElement.src = reader.result as string;
        resolve({ alignment });
      };
      reader.readAsDataURL(audioBlob);
    });
  }

  return { alignment };
};

// Check if MediaSource is supported
export const isMediaSourceSupported = (): boolean => {
  return typeof MediaSource !== "undefined" && MediaSource.isTypeSupported && MediaSource.isTypeSupported("audio/mpeg");
};

// Initialize MediaSource for streaming audio
const initializeMediaSource = (
  audioElement: HTMLAudioElement,
): Promise<{ mediaSource: MediaSource; sourceBuffer: SourceBuffer }> => {
  return new Promise((resolve, reject) => {
    if (!isMediaSourceSupported()) {
      reject(new Error("MediaSource not supported"));
      return;
    }

    const mediaSource = new MediaSource();
    audioElement.src = URL.createObjectURL(mediaSource);

    mediaSource.addEventListener("sourceopen", () => {
      try {
        // Using a common audio format that's supported
        const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
        resolve({ mediaSource, sourceBuffer });
      } catch (error) {
        reject(error);
      }
    });

    mediaSource.addEventListener("error", reject);
  });
};

// Append audio chunk to source buffer
const appendAudioChunk = (sourceBuffer: SourceBuffer, chunk: Uint8Array): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (sourceBuffer.updating) {
      sourceBuffer.addEventListener(
        "updateend",
        () => {
          appendAudioChunk(sourceBuffer, chunk).then(resolve).catch(reject);
        },
        { once: true },
      );
      return;
    }

    try {
      sourceBuffer.appendBuffer(chunk as BufferSource);
      sourceBuffer.addEventListener("updateend", () => resolve(), { once: true });
    } catch (error) {
      reject(error);
    }
  });
};

type StreamingState = {
  audioElement: HTMLAudioElement;
  bufferedDuration: number;
  completeAudioChunks: Uint8Array[];
  hasAllAudio: boolean;
  isFirstChunk: boolean;
  isManuallyPaused: boolean;
  mediaSource: MediaSource | null;
  sourceBuffer: SourceBuffer | null;
};

const initializeStreamingState = async (audioElement: HTMLAudioElement): Promise<StreamingState> => {
  const { mediaSource, sourceBuffer } = await initializeMediaSource(audioElement);
  return {
    mediaSource,
    sourceBuffer,
    isFirstChunk: true,
    audioElement,
    bufferedDuration: 0,
    isManuallyPaused: false,
    completeAudioChunks: [],
    hasAllAudio: false,
  };
};

// Amount of audio to buffer before starting playback (in seconds)
const MINIMUM_BUFFER_SIZE = 2;
// When buffer drops below this, we'll pause to rebuild buffer (in seconds)
const BUFFER_UNDERRUN_THRESHOLD = 1;

const createStreamingCleanup =
  (state: StreamingState, handlers?: { handlePause: () => void; handlePlay: () => void }) => () => {
    if (state.audioElement) {
      state.audioElement.pause();
      state.audioElement.currentTime = 0;
    }

    // Remove event listeners if handlers were provided
    if (handlers) {
      state.audioElement.removeEventListener("pause", handlers.handlePause);
      state.audioElement.removeEventListener("play", handlers.handlePlay);
    }

    // Clean up media source
    if (state.mediaSource && state.mediaSource.readyState === "open") {
      try {
        state.mediaSource.endOfStream();
      } catch {
        // Ignore errors when cleaning up
      }
    }

    // Revoke object URL to prevent memory leaks
    if (state.audioElement.src && state.audioElement.src.startsWith("blob:")) {
      URL.revokeObjectURL(state.audioElement.src);
    }

    state.mediaSource = null;
    state.sourceBuffer = null;
  };

const handleAudioChunk = async (state: StreamingState, audioData: Uint8Array) => {
  if (!state.sourceBuffer) return;

  await appendAudioChunk(state.sourceBuffer, audioData);

  // Find the current buffer range and calculate how much is buffered ahead
  const currentTime = state.audioElement.currentTime;
  let bufferedAhead = 0;

  for (let i = 0; i < state.sourceBuffer.buffered.length; i++) {
    const rangeStart = state.sourceBuffer.buffered.start(i);
    const rangeEnd = state.sourceBuffer.buffered.end(i);

    // If current time is within or before this range
    if (currentTime <= rangeEnd) {
      if (currentTime >= rangeStart) {
        // Current time is within this range
        bufferedAhead = rangeEnd - currentTime;
      } else {
        // Current time is before this range
        bufferedAhead = rangeEnd - rangeStart;
      }
      break;
    }
  }
  state.bufferedDuration = bufferedAhead;

  // Handle buffer underrun
  if (!state.isFirstChunk && state.audioElement) {
    if (state.audioElement.paused && !state.isManuallyPaused && bufferedAhead >= MINIMUM_BUFFER_SIZE) {
      // We have enough buffer to resume playback
      state.audioElement.play().catch(error => {
        logger.warn("Resume playback failed:", error);
      });
    } else if (!state.audioElement.paused && bufferedAhead < BUFFER_UNDERRUN_THRESHOLD) {
      // Buffer is too low, pause to rebuild
      state.audioElement.pause();
      state.isManuallyPaused = false; // This was an automatic pause
    }
  }

  // Start playing after we have enough buffer
  if (state.isFirstChunk && state.audioElement && bufferedAhead >= MINIMUM_BUFFER_SIZE) {
    state.audioElement.play().catch(error => {
      logger.warn("Auto-play failed:", error);
    });
    state.isFirstChunk = false;
  }
};

const createPausePlayHandlers = (state: StreamingState) => {
  const handlePause = () => {
    // Only set manual pause if it wasn't triggered by buffer underrun
    if (!state.audioElement.paused || state.bufferedDuration >= BUFFER_UNDERRUN_THRESHOLD) {
      state.isManuallyPaused = true;
    }
  };

  const handlePlay = () => {
    // Only clear manual pause if we have enough buffer or all audio
    if (state.bufferedDuration >= MINIMUM_BUFFER_SIZE || state.hasAllAudio) {
      state.isManuallyPaused = false;
    } else {
      // If we don't have enough buffer and don't have all audio, keep it paused
      state.audioElement.pause();
    }
  };

  return { handlePause, handlePlay };
};

const handleStreamComplete = (state: StreamingState, onStreamComplete?: (audioData: Uint8Array) => void) => {
  // When stream is complete, combine all chunks and notify
  if (state.completeAudioChunks.length > 0) {
    const totalLength = state.completeAudioChunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const completeAudio = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of state.completeAudioChunks) {
      completeAudio.set(chunk, offset);
      offset += chunk.length;
    }
    onStreamComplete?.(completeAudio);
    state.hasAllAudio = true;

    // If we haven't started playing yet (short audio), start now
    if (state.isFirstChunk && state.audioElement) {
      state.audioElement.play().catch(error => {
        logger.warn("Auto-play failed:", error);
      });
      state.isFirstChunk = false;
    }
  }
};

const generateDialogueNonStreaming = async ({
  audioElement,
  config,
  abortController,
}: {
  abortController?: AbortController;
  audioElement: HTMLAudioElement;
  config: DialogueConfig;
}) => {
  const response = await fetch(`${SERVER_URL}/v1/text-to-dialogue-anonymous`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(config),
    signal: abortController?.signal,
  });

  if (response.ok) {
    if (response.body !== null) {
      // Handle regular blob response
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = () => {
        audioElement.src = reader.result as string;
      };
      reader.readAsDataURL(blob);
      return null;
    } else {
      const { detail } = await response.json();
      throw detail;
    }
  }
};

export const generateDialogueTts = ({
  audioElement,
  config,
  abortController,
  onStreamComplete,
}: {
  abortController?: AbortController;
  audioElement: HTMLAudioElement;
  config: DialogueConfig;
  onStreamComplete?: (audioData: Uint8Array) => void;
}): {
  cleanup: () => void;
  promise: Promise<void>;
} => {
  const state: StreamingState = {
    mediaSource: null,
    sourceBuffer: null,
    isFirstChunk: true,
    audioElement,
    bufferedDuration: 0,
    isManuallyPaused: false,
    completeAudioChunks: [],
    hasAllAudio: false,
  };
  const { handlePause, handlePlay } = createPausePlayHandlers(state);
  const cleanup = createStreamingCleanup(state, { handlePause, handlePlay });

  const streamingPromise = (async () => {
    try {
      if (abortController?.signal.aborted) {
        return;
      }

      // Check if MediaSource is supported, if not fall back to non-streaming
      if (!isMediaSourceSupported()) {
        logger.warn("MediaSource not supported, falling back to non-streaming TTS");
        await generateDialogueNonStreaming({
          audioElement,
          config,
          abortController,
        });

        audioElement.play();
        return;
      }

      // Set up event listeners for streaming
      audioElement.addEventListener("pause", handlePause);
      audioElement.addEventListener("play", handlePlay);

      const streamState = await initializeStreamingState(audioElement);
      Object.assign(state, streamState);

      if (abortController?.signal.aborted) {
        return;
      }

      const streamingUrl = `${SERVER_URL}/v1/text-to-dialogue-anonymous/stream`;

      const response = await fetch(streamingUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(config),
        signal: abortController?.signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData.detail;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body reader available");
      }

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            handleStreamComplete(state, onStreamComplete);
            break;
          }

          if (abortController?.signal.aborted) {
            await reader.cancel();
            return;
          }

          const audioChunk = new Uint8Array(value);
          state.completeAudioChunks.push(audioChunk);
          await handleAudioChunk(state, audioChunk);
        }

        // End the media source
        if (state.mediaSource && state.mediaSource.readyState === "open") {
          state.mediaSource.endOfStream();
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      cleanup();
      throw error;
    }
  })();

  return {
    cleanup,
    promise: streamingPromise,
  };
};

export const generateTts = async ({
  audioElement,
  config,
  voiceId,
  modelId,
  speed = 1,
  withTimestamps = false,
  abortController,
  hcaptcha_token,
  urlOverride,
  additionalHeaders,
}: {
  abortController?: AbortController;
  additionalHeaders?: Record<string, string>;
  audioElement: HTMLAudioElement;
  config: { language_code?: string; text: string };
  hcaptcha_token?: null | string;
  modelId: string;
  speed?: number;
  urlOverride?: string;
  voiceId: string;
  withTimestamps?: boolean;
}): Promise<{ alignment?: Alignment } | null | undefined> => {
  const requestBody = {
    text: config.text,
    model_id: modelId,
    voice_settings: {
      speed,
    },
    hcaptcha_token,
    ...(config.language_code && { language_code: config.language_code }),
  };

  // Make API request
  const url =
    urlOverride ?? `${SERVER_URL}/v1/text-to-speech/${voiceId}${withTimestamps ? "/with-timestamps" : ""}/anonymous`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...additionalHeaders,
    },
    body: JSON.stringify(requestBody),
    signal: abortController?.signal,
  });

  if (res.ok) {
    if (withTimestamps) {
      // Handle JSON response with timestamps
      const data = await res.json();
      return processTimestampData(data, audioElement);
    } else if (res.body !== null) {
      // Handle regular blob response
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onload = () => {
        audioElement.src = reader.result as string;
      };
      reader.readAsDataURL(blob);
      return null;
    }
  } else {
    const { detail } = await res.json();
    throw detail;
  }
};

export const generateStreamingTts = ({
  audioElement,
  config,
  voiceId,
  modelId,
  speed = 1,
  onTimestampsReceived,
  abortController,
  onStreamComplete,
  hcaptcha_token,
  urlOverride,
  fallbackUrlOverride,
  additionalHeaders,
}: {
  abortController?: AbortController;
  additionalHeaders?: Record<string, string>;
  audioElement: HTMLAudioElement;
  config: { language_code?: string; text: string };
  fallbackUrlOverride?: string;
  hcaptcha_token?: null | string;
  modelId: string;
  onStreamComplete?: (audioData: Uint8Array) => void;
  onTimestampsReceived: (response: StreamResponse) => void;
  speed?: number;
  urlOverride?: string;
  voiceId: string;
}): { cleanup: () => void; promise: Promise<void> } => {
  const state: StreamingState = {
    mediaSource: null,
    sourceBuffer: null,
    isFirstChunk: true,
    audioElement,
    bufferedDuration: 0,
    isManuallyPaused: false,
    completeAudioChunks: [],
    hasAllAudio: false,
  };
  const { handlePause, handlePlay } = createPausePlayHandlers(state);
  const cleanup = createStreamingCleanup(state, { handlePause, handlePlay });

  const streamingPromise = (async () => {
    try {
      if (abortController?.signal.aborted) {
        return;
      }

      // Check if MediaSource is supported, if not fall back to non-streaming
      if (!isMediaSourceSupported()) {
        logger.warn("MediaSource not supported, falling back to non-streaming TTS");
        const result = await generateTts({
          audioElement,
          config,
          voiceId,
          modelId,
          speed,
          withTimestamps: true,
          abortController,
          hcaptcha_token,
          urlOverride: fallbackUrlOverride,
          additionalHeaders,
        });

        if (result?.alignment) {
          onTimestampsReceived({
            alignment: result.alignment,
            normalized_alignment: null,
          });
          audioElement.play();
        }
        return;
      }

      // Set up event listeners for streaming
      audioElement.addEventListener("pause", handlePause);
      audioElement.addEventListener("play", handlePlay);

      const streamState = await initializeStreamingState(audioElement);
      Object.assign(state, streamState);

      const requestBody = {
        text: config.text,
        model_id: modelId,
        voice_settings: {
          speed,
        },
        hcaptcha_token,
        ...(config.language_code && { language_code: config.language_code }),
      };

      const url = urlOverride ?? `${SERVER_URL}/v1/text-to-speech/${voiceId}/stream/with-timestamps/anonymous`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...additionalHeaders,
        },
        body: JSON.stringify(requestBody),
        signal: abortController?.signal,
      });

      if (!res.ok) {
        const { detail } = await res.json();
        throw detail;
      }

      if (!res.body) {
        throw new Error("No response body available");
      }

      const reader = res.body.getReader();
      let buffer = "";
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            handleStreamComplete(state, onStreamComplete);
            break;
          }

          const text = decoder.decode(value);
          for (let i = 0; i < text.length; i++) {
            buffer += text[i];
            if (text[i] === "}") {
              try {
                const chunkData = JSON.parse(buffer);

                if (chunkData.audio_base64) {
                  const audioData = Uint8Array.from(atob(chunkData.audio_base64), c => c.charCodeAt(0));
                  // Store chunk for complete audio
                  state.completeAudioChunks.push(audioData);
                  await handleAudioChunk(state, audioData);
                }

                if (chunkData.alignment || chunkData.normalized_alignment) {
                  onTimestampsReceived({
                    alignment: chunkData.alignment || null,
                    normalized_alignment: chunkData.normalized_alignment || null,
                  });
                }

                buffer = "";
              } catch {
                // Not a complete/valid JSON object yet, continue buffering
              }
            }
          }
        }

        if (state.mediaSource && state.mediaSource.readyState === "open") {
          state.mediaSource.endOfStream();
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      // Only throw if it's not a manual pause
      if (!state.isManuallyPaused) {
        logger.error(error instanceof Error ? error : null, "TTS streaming error");
        cleanup();
        throw error;
      }
    }
  })();

  return {
    cleanup,
    promise: streamingPromise,
  };
};

export const errorMap: { [key: string]: { description: string; title: string } } = {
  quota_exceeded: {
    title: "Quota Exceeded",
    description:
      "You have reached the limit of available requests on the landing page. You can continue, for free, by signing-up to the platform.",
  },
  system_busy: {
    title: "Sytem Busy",
    description: "The system is currently experiencing unusually high levels of activity. Please try again later.",
  },
};
